/**
 * Onboarding API client for payment-first user registration
 */
import apiClient from './client';

// =============================================================================
// Types
// =============================================================================

export interface VerifyPaymentRequest {
  email: string;
  tier?: string;  // From URL param, e.g., "basic", "professional"
  period?: string;  // From URL param, e.g., "monthly", "yearly"
}

export interface VerifyPaymentResponse {
  verified: boolean;
  message: string;
  verification_token: string | null;
  subscription_tier: string | null;
  email: string | null;
}

export interface CompleteOnboardingRequest {
  verification_token: string;
  password: string;
  confirm_password: string;
  full_name?: string;
  sms_opt_in?: boolean;
}

export interface CompleteOnboardingResponse {
  success: boolean;
  message: string;
  access_token: string | null;
  refresh_token: string | null;
  token_type: string;
  user_id: string | null;
  email: string | null;
  subscription_tier: string | null;
}

export interface PasswordRequirements {
  min_length: number;
  max_length: number;
  requires_uppercase: boolean;
  requires_lowercase: boolean;
  requires_number: boolean;
  requires_special: boolean;
  message: string;
}

export interface OnboardingStatus {
  valid: boolean;
  status: string;
  email?: string;
  subscription_tier?: string;
  expires_at?: string;
  message?: string;
}

// =============================================================================
// API Functions
// =============================================================================

/**
 * Get password requirements for display
 */
export const getPasswordRequirements = async (): Promise<PasswordRequirements> => {
  const response = await apiClient.get<PasswordRequirements>('/api/onboarding/password-requirements');
  return response.data;
};

/**
 * Verify payment by email address
 * @param email User's email address
 * @param tier Subscription tier from URL params (e.g., "basic", "professional")
 * @param period Billing period from URL params (e.g., "monthly", "yearly")
 */
export const verifyPayment = async (
  email: string,
  tier?: string,
  period?: string
): Promise<VerifyPaymentResponse> => {
  const response = await apiClient.post<VerifyPaymentResponse>('/api/onboarding/verify', {
    email,
    tier,
    period,
  });
  return response.data;
};

/**
 * Complete onboarding by creating account with password
 */
export const completeOnboarding = async (
  verificationToken: string,
  password: string,
  confirmPassword: string,
  fullName?: string,
  smsOptIn?: boolean
): Promise<CompleteOnboardingResponse> => {
  const response = await apiClient.post<CompleteOnboardingResponse>('/api/onboarding/complete', {
    verification_token: verificationToken,
    password,
    confirm_password: confirmPassword,
    full_name: fullName,
    sms_opt_in: smsOptIn,
  });
  return response.data;
};

/**
 * Check onboarding token status
 */
export const getOnboardingStatus = async (token: string): Promise<OnboardingStatus> => {
  const response = await apiClient.get<OnboardingStatus>(`/api/onboarding/status/${token}`);
  return response.data;
};
